<template>
  <div class="login-container">
    <div class="login-left">
      <div class="logo-section">
        <img src="@/assets/images/login/logo.png" alt="logo" class="logo-image">
        <h2 class="logo-subtitle">辅助创新</h2>
        <p class="logo-description">明确需求与边界，匹配可行技术路线<br>输出完整解决方案</p>
      </div>
    </div>
    <div class="login-box">
      <div class="login-inner">
        <div class="login-header">
          <h2>欢迎回来</h2>
          <p>请输入您的账号信息以登录</p>
        </div>
        <!-- 登录表单 -->
        <el-form v-if="isLogin" :model="loginForm" :rules="loginRules" ref="loginFormRef">
          <el-form-item prop="username" label="账号">
            <el-input 
              v-model="loginForm.username"
              placeholder="example@email.com"
              @keyup.enter="handleLogin"
            />
          </el-form-item>
          <div class="password-wrapper">
            <el-form-item prop="password" label="密码">
              <el-input 
                v-model="loginForm.password"
                type="password"
                placeholder="密码"
                show-password
                @keyup.enter="handleLogin"
              />
            </el-form-item>
          </div>

            <el-form-item>
            <el-button 
              type="primary" 
              size="large"
              :loading="loading"
              @click="handleLogin" 
              style="width: 100%;"
              class="login-button"
            >
              登录
            </el-button>
          </el-form-item>
          <div class="form-footer">
            <a href="#" class="forgot-password" @click.prevent>忘记密码？</a>
          </div>
          <div class="register-link">
            <span>还没有账号？</span>
            <a href="#" @click.prevent="handleChangeLogin">立即注册</a>
          </div>
        </el-form>
        <!-- 注册表单 -->
        <el-form v-else :model="registerForm" :rules="registerRules" ref="registerFormRef">
          <el-form-item prop="username" label="用户名">
            <el-input 
              v-model="registerForm.username"
              placeholder="请输入用户名"
              :prefix-icon="User"
            />
          </el-form-item>
          <el-form-item prop="realname" label="昵称">
            <el-input 
              v-model="registerForm.realname"
              placeholder="请输入昵称"
              :prefix-icon="User"
            />
          </el-form-item>
          <el-form-item prop="password" label="密码">
            <el-input 
              v-model="registerForm.password"
              type="password"
              placeholder="请输入密码"
              :prefix-icon="Lock"
              show-password
            />
          </el-form-item>
          <el-form-item prop="confirmPassword" label="确认密码">
            <el-input 
              v-model="registerForm.confirmPassword"
              type="password"
              placeholder="请再次输入密码"
              :prefix-icon="Lock"
              show-password
            />
          </el-form-item>
          <el-form-item
            label="联系电话"
            prop="phone"
          >
            <el-input v-model="registerForm.phone" placeholder="请输入联系电话" />
          </el-form-item>
          <el-form-item label="验证码" prop="smsCode" class="form-item-code" >
            <el-input v-model="registerForm.smsCode" placeholder="请输入验证码" />
            <el-button type="primary" plain @click="() => sendCode(registerForm.phone)" :disabled="isSending || countdown > 0 || !regexp.phone.test(registerForm.phone)">
              {{ countdown > 0 ? `${countdown} 秒后重新获取` : '获取验证码' }}
            </el-button>
          </el-form-item>
          <div class="read-wrapper">
            <el-checkbox v-model="registerReaded">
              我已阅读并同意<a>《服务协议》</a>和<a>《隐私协议》</a>
            </el-checkbox>
          </div>
          <el-form-item>
            <el-button 
              type="primary" 
              size="large"
              :loading="registerLoading"
              @click="handleRegister" 
              style="width: 100%;"
              class="login-button"
            >
              注册
            </el-button>
          </el-form-item>
          <div class="register-link">
            <span>已有账号？</span>
            <a href="#" @click.prevent="handleChangeLogin">返回登录</a>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { User, Lock } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import { Vue3Lottie } from 'vue3-lottie'
import { loginApi, registerApi } from '@/api/login'
import { useUserStore } from '@/store/modules/user';
import { useSendCode } from '@/hooks/useSendCode'
import encrypt from '@/utils/encrypt'
import regexp from '@/utils/regexp'
import { ensureUserProfileInitialized } from '@/utils/userProfile'

const userStore = useUserStore()
const router = useRouter()
const loginFormRef = ref(null)
const registerFormRef = ref(null)
const isLogin = ref(true)

const { sendCode, isSending, countdown, clear } = useSendCode();

// 登录表单相关
const loginForm = reactive({
  username: '',
  password: ''
})

const loginRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ],
}

// 注册表单相关
const registerForm = reactive({
  username: '',
  realname: '',
  phone: '',
  smsCode: '',
  password: '',
  confirmPassword: ''
})

const validatePass = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请输入密码'))
  } else {
    if (registerForm.confirmPassword !== '') {
      registerFormRef.value.validateField('confirmPassword')
    }
    callback()
  }
}

const validatePass2 = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请再次输入密码'))
  } else if (value !== registerForm.password) {
    callback(new Error('两次输入密码不一致!'))
  } else {
    callback()
  }
}

const registerRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
  ],
  realname: [
    { required: true, message: '请输入昵称', trigger: 'blur' },
    { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
  ],
  phone: [
    { required: true, message: '请输入联系电话', trigger: 'blur' },
    { pattern: regexp.phone, message: '手机号码格式有误' },
  ],
  smsCode: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
  ],
  password: [
    { required: true, validator: validatePass, trigger: 'blur' },
    {
      pattern: /^.{6,}$/,
      message: '密码由6位符号组成!',
      trigger: 'blur'
    },
  ],
  confirmPassword: [
    { required: true, validator: validatePass2, trigger: 'blur' }
  ]
}

const autoLogin = ref(false)
const readed = ref(false)
const registerReaded = ref(false)
const loading = ref(false)
const registerLoading = ref(false)

const handleChangeLogin = () => {
  isLogin.value = !isLogin.value
  loginFormRef.value?.resetFields()
  registerFormRef.value?.resetFields()
  registerReaded.value = false
  // 离开注册表单时停掉验证码倒计时
  if (isLogin.value) clear()
}

const handleLogin = () => {
  loginFormRef.value.validate((valid) => {
    if (!valid) return
    loading.value = true
    loginApi({ username: loginForm.username, password:encrypt(loginForm.password) })
      .then(res => {
        if (res) {
          const redirect = router.currentRoute.value.query.redirect
          userStore.setToken(res.token)
          ensureUserProfileInitialized().catch(() => {})
          ElMessage.success('登录成功')
          // 跳转到IPT页面
          router.push(redirect ? decodeURIComponent(redirect) : '/ipt')
        }
      })
      .catch(err => {
        ElMessage.error(err?.message || '登录失败，请稍后重试')
      })
      .finally(() => {
        // 无论成功失败都要解除 loading，否则按钮会一直转圈
        loading.value = false
      })
  })
}

const handleRegister = () => {
  registerFormRef.value.validate((valid) => {
    if (!valid) return
    registerLoading.value = true
    registerApi({
      username: registerForm.username,
      realname: registerForm.realname,
      phone: encrypt(registerForm.phone),
      smsCode: registerForm.smsCode,
      password: encrypt(registerForm.password),
      roleCode: 'xinzhishu_normal'
    })
      .then(res => {
        // registerApi 用 raw 模式返回完整响应体：success=false 时把后端原因显示出来
        if (res && res.success !== false) {
          ElMessage.success('注册成功，请使用新账号登录')
          registerFormRef.value?.resetFields()
          registerReaded.value = false
          clear()
          isLogin.value = true
        } else {
          ElMessage.error((res && res.message) || '注册失败，请检查填写的信息')
        }
      })
      .catch(err => {
        ElMessage.error(err?.message || '注册失败，请稍后重试')
      })
      .finally(() => {
        registerLoading.value = false
      })
  })
}
</script>

<style scoped lang="scss">
$border-color: #E7E7E9;

.login-container {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  background-image: url('@/assets/images/login/bg.png');
  background-repeat: no-repeat;
  background-size: 100% 100%;
  overflow: auto;
  position: relative;
}

.login-left {
  position: absolute;
  top: 150px;
  left: 8%;
}

.logo-section {
  text-align: left;
  
  .logo {
    font-size: 48px;
    font-weight: 700;
    color: #000;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
  }
  .logo-image {
    width: 300px;
    height: auto;
    margin-bottom: 12px;
  }
  
  .logo-subtitle {
    font-size: 30px;
    font-weight: 600;
    color: #333;
    margin: 0 0 20px 0;
  }
  
  .logo-description {
    font-size: 20px;
    color: #666;
    line-height: 28px;
    margin: 0;
  }
}
.login-box {
  position: relative;
  width: 532px;
  font-weight: 500;
  box-sizing: border-box;
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  .login-logo {
    display: block;
    width: auto;
    height: 30px;
    margin: 0 auto 46px;
  }
  .login-inner {
      flex: 1;
      display: flex;
      flex-direction: column;
      
      .login-header {
        margin-bottom: 30px;
        
        h2 {
          font-size: 24px;
          font-weight: 600;
          color: #000;
          margin-bottom: 8px;
        }
        
        p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }
      }
      
      :deep(.el-button) {
        width: 100%;
        height: 50px;
        line-height: 50px;
        border-radius: 8px;
        border: none;
        background: #000;
        color: #fff;
        font-size: 16px;
        font-weight: 500;
        
        &.el-button--primary:not(.is-plain) {
          color: #fff;
          background: #000;
          &:hover,
          &:focus {
            background: #333;
            box-shadow: none;
          }
        }
      }
    }
  :deep(.el-form) {
    flex: 1;
    display: flex;
    flex-direction: column;
    
    .el-form-item {
      flex-direction: column;
      justify-content: flex-start;
      margin-bottom: 24px;
      
      .el-form-item__label {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        margin-bottom: 8px;
        justify-content: flex-start;
      }
    }
    
    .el-form-item__error {
      font-size: 12px;
    }
    
    .el-input__wrapper {
      // padding: 14px 16px;
      background: #fff;
      box-shadow: none;
      border: 1px solid $border-color;
      border-radius: 8px;
      transition: all 0.3s ease;
      
      &:hover {
        box-shadow: none;
      }
      
      &.is-focus {
        box-shadow: none;
        border-color: #000;
      }
    }
    .password-wrapper {
      position: relative;
    }
  }
}


.form-item-code {
  :deep(.el-form-item__content) {
    position: relative;
    padding-right: calc(40% + 20px);
    .el-button {
      position: absolute;
      right: 0;
      width: 40%;
      height: 100%;
      border-radius: 12px;
      border: none;
    }
  }
}

:deep(.el-input__wrapper.is-focus) {
  background: white;
}

:deep(.el-input__inner) {
  height: 48px;
  font-size: 16px;
  padding: 0;
}

:deep(.el-checkbox__label) {
  margin-bottom: 0;
  color: #aaa;
  font-size: 14px;
  font-weight: 400;
}

.checkbox-wrapper {
  margin-bottom: 0 !important;
}

.form-footer {
  margin-top: 8px;
  margin-bottom: 24px;
  display: flex;
  justify-content: flex-end;
  
  .forgot-password {
    color: #666;
    font-size: 14px;
    text-decoration: none;
  }
  
  .forgot-password:hover {
    color: #000;
  }
}

.register-link {
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #666;
  
  a {
    color: #000;
    text-decoration: none;
    font-weight: 500;
  }
}



@media screen and (max-width: 1440px){
  .login-container {
    .login-box {
      width: 90%;
      max-width: 532px;
      padding: 30px;
      
      .login-logo {
        margin: 0 auto 20px;
      }
      .login-inner {
        :deep(.el-button) {
          height: 46px;
          line-height: 46px;
        }
      }
      :deep(.el-form) {
        padding-top: 0;
        .el-form-item {
          margin-bottom: 20px;
          .el-input__wrapper {
            padding: 12px 16px;
          }
        }
        :deep(.el-input__inner) {
          height: 44px;
        }
      }
    }
  }
}
</style> 
